import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import Swal from "sweetalert2";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showModal, hideModal, installModalFallback } from "./modalHelper";
import {
    API, TYPES_JOURNAL, GRAVITES, findTypeJournal, findGravite, unwrap, apiMessage, toast, mediaKind,
} from "./projetsCommon";

const ENTRY_MODAL = "journalEntryModal";
const PHOTO_MODAL = "journalPhotoModal";

const nowInput = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().substring(0, 16);                 // yyyy-MM-ddTHH:mm
};
const toDateTimeInput = (v) => (v ? String(v).substring(0, 16) : "");
const dayKey = (v) => String(v).substring(0, 10);
const formatDay = (v) => new Date(v).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const formatTime = (v) => new Date(v).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

const empty = () => ({ date: nowInput(), type: 1, titre: "", contenu: "", auteurId: "", gravite: 2, resolu: false });

const schema = Yup.object({
    date: Yup.string().required("La date est obligatoire"),
    titre: Yup.string().trim().required("Le titre est obligatoire").max(200, "200 caractères maximum"),
    contenu: Yup.string().nullable().max(4000, "4000 caractères maximum"),
});

/** Onglet « Journal » : notes, incidents, réunions, visites… avec photos. */
const ProjetJournal = ({ projetId, employes }) => {
    const [journal, setJournal] = useState(null);
    const [typeFilter, setTypeFilter] = useState("");
    const [openIncidentsOnly, setOpenIncidentsOnly] = useState(false);
    const [editing, setEditing] = useState(null);

    // Photos
    const [thumbs, setThumbs] = useState({});
    const thumbsRef = useRef({});
    const [photoEntry, setPhotoEntry] = useState(null);
    const [folders, setFolders] = useState([]);
    const [folderId, setFolderId] = useState("");
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [projetDocs, setProjetDocs] = useState([]);

    useEffect(() => installModalFallback(), []);
    useEffect(() => {
        load();
        return () => {
            Object.values(thumbsRef.current).forEach((u) => window.URL.revokeObjectURL(u));
            thumbsRef.current = {};
        };
    }, [projetId]); // eslint-disable-line react-hooks/exhaustive-deps

    const load = () =>
        axiosInstance.get(API.journal(projetId))
            .then((res) => {
                const data = unwrap(res);
                setJournal(data);
                loadThumbs(data?.entrees || []);
            })
            .catch((error) => toast("error", apiMessage(error, "Impossible de charger le journal")));

    const loadThumbs = (entries) => {
        entries.flatMap((e) => e.photos)
            .filter((p) => mediaKind(p) === "image" && !thumbsRef.current[p.documentId])
            .forEach(async (p) => {
                try {
                    const r = await axiosInstance.get(API.preview(p.documentId), { responseType: "blob" });
                    thumbsRef.current = { ...thumbsRef.current, [p.documentId]: window.URL.createObjectURL(r.data) };
                    setThumbs({ ...thumbsRef.current });
                } catch { /* miniature indisponible */ }
            });
    };

    const openPreview = async (p) => {
        const tab = window.open("", "_blank");
        try {
            const r = await axiosInstance.get(API.preview(p.documentId), { responseType: "blob" });
            const url = window.URL.createObjectURL(r.data);
            if (tab) tab.location.href = url;
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        } catch { if (tab) tab.close(); toast("error", "Aperçu indisponible"); }
    };

    // ---------- Entrées ----------
    const formik = useFormik({
        initialValues: empty(),
        validationSchema: schema,
        onSubmit: async (v, { setErrors }) => {
            const incident = Number(v.type) === 2;
            const payload = {
                date: v.date,
                type: Number(v.type),
                titre: v.titre.trim(),
                contenu: v.contenu?.trim() || null,
                auteurId: v.auteurId ? Number(v.auteurId) : null,
                gravite: incident ? Number(v.gravite) : null,
                resolu: incident && !!v.resolu,
            };
            try {
                const res = editing
                    ? await axiosInstance.put(API.journalEntry(projetId, editing.id), payload)
                    : await axiosInstance.post(API.journal(projetId), payload);
                hideModal(ENTRY_MODAL);
                toast("success", editing ? "Entrée modifiée" : "Entrée ajoutée");
                await load();
                // Nouvelle entrée : on propose tout de suite d'ajouter des photos
                if (!editing) {
                    const created = unwrap(res);
                    if (created?.id) {
                        const r = await Swal.fire({
                            title: "Ajouter des photos ?", icon: "question",
                            showCancelButton: true, confirmButtonText: "Oui", cancelButtonText: "Plus tard",
                        });
                        if (r.isConfirmed) openPhotos(created);
                    }
                }
            } catch (error) {
                if (error.response?.data?.errors) setErrors(error.response.data.errors);
                else toast("error", apiMessage(error, "Enregistrement échoué"));
            }
        },
    });

    const openEntry = (e) => {
        setEditing(e || null);
        formik.resetForm({
            values: e ? {
                date: toDateTimeInput(e.date), type: findTypeJournal(e.type).value, titre: e.titre,
                contenu: e.contenu ?? "", auteurId: e.auteurId ?? "",
                gravite: findGravite(e.gravite)?.value ?? 2, resolu: !!e.resolu,
            } : empty(),
        });
        showModal(ENTRY_MODAL);
    };

    const removeEntry = (e) => {
        Swal.fire({
            title: `Supprimer « ${e.titre} » ?`, text: "Les photos restent dans la galerie du projet.", icon: "warning",
            showCancelButton: true, confirmButtonText: "Supprimer", cancelButtonText: "Annuler",
        }).then(async (r) => {
            if (!r.isConfirmed) return;
            try { await axiosInstance.delete(API.journalEntry(projetId, e.id)); toast("success", "Entrée supprimée"); load(); }
            catch (error) { toast("error", apiMessage(error, "Suppression échouée")); }
        });
    };

    const toggleResolu = async (e) => {
        try {
            await axiosInstance.put(API.journalEntry(projetId, e.id), {
                date: e.date, type: findTypeJournal(e.type).value, titre: e.titre, contenu: e.contenu,
                auteurId: e.auteurId, gravite: findGravite(e.gravite)?.value ?? 2, resolu: !e.resolu,
            });
            toast("success", e.resolu ? "Incident rouvert" : "Incident résolu");
            load();
        } catch (error) { toast("error", apiMessage(error, "Mise à jour échouée")); }
    };

    // ---------- Photos ----------
    const openPhotos = (entry) => {
        setPhotoEntry(entry);
        setFiles([]);
        if (folders.length === 0) {
            axiosInstance.get(API.gedFolders).then((res) => setFolders(unwrap(res) || [])).catch(() => setFolders([]));
        }
        axiosInstance.get(API.projetDocuments(projetId)).then((res) => setProjetDocs(unwrap(res) || [])).catch(() => setProjetDocs([]));
        showModal(PHOTO_MODAL);
    };

    const linkPhoto = (entryId, documentId) => axiosInstance.post(API.journalPhotos(projetId, entryId), { documentId });

    // Envoi : GED (upload) → rattachement au projet → rattachement à l'entrée
    const uploadPhotos = async () => {
        if (!folderId || files.length === 0) return;
        setUploading(true);
        const failures = [];
        for (const file of files) {
            try {
                const fd = new FormData();
                fd.append("file", file);
                fd.append("folderId", folderId);
                fd.append("description", photoEntry.titre);
                const up = await axiosInstance.post(API.gedUpload, fd, { headers: { "Content-Type": "multipart/form-data" } });
                const doc = unwrap(up);
                const documentId = doc?.id ?? doc?.Id;
                if (!documentId) throw new Error("L'API d'upload n'a pas renvoyé l'identifiant du document");
                await axiosInstance.post(API.documentProjets, { documentId, projetId, commentaire: photoEntry.titre });
                await linkPhoto(photoEntry.id, documentId);
            } catch (error) {
                failures.push(`${file.name} : ${apiMessage(error, error.message || "erreur")}`);
            }
        }
        setUploading(false);
        setFiles([]);
        load();
        if (failures.length === 0) {
            hideModal(PHOTO_MODAL);
            toast("success", "Photos ajoutées");
        } else {
            Swal.fire({ icon: "warning", title: "Certaines photos n'ont pas été ajoutées", html: failures.map((f) => `<div class="text-left small">${f}</div>`).join("") });
        }
    };

    const pickExisting = async (documentId) => {
        try {
            await linkPhoto(photoEntry.id, documentId);
            toast("success", "Photo ajoutée");
            load();
            setPhotoEntry((p) => ({ ...p, photos: [...(p.photos || []), { documentId }] }));
        } catch (error) { toast("error", apiMessage(error, "Ajout impossible")); }
    };

    const unlinkPhoto = (entry, p) => {
        Swal.fire({
            title: "Retirer cette photo de l'entrée ?", text: "Elle reste dans la galerie du projet.", icon: "question",
            showCancelButton: true, confirmButtonText: "Retirer", cancelButtonText: "Annuler",
        }).then(async (r) => {
            if (!r.isConfirmed) return;
            try { await axiosInstance.delete(API.journalPhoto(projetId, entry.id, p.documentId)); load(); }
            catch (error) { toast("error", apiMessage(error, "Opération échouée")); }
        });
    };

    // ---------- Affichage ----------
    const grouped = useMemo(() => {
        const list = (journal?.entrees || []).filter((e) => {
            if (typeFilter && findTypeJournal(e.type).value !== Number(typeFilter)) return false;
            if (openIncidentsOnly && !(findTypeJournal(e.type).value === 2 && !e.resolu)) return false;
            return true;
        });
        const groups = [];
        list.forEach((e) => {
            const k = dayKey(e.date);
            const last = groups[groups.length - 1];
            if (last && last.key === k) last.items.push(e);
            else groups.push({ key: k, date: e.date, items: [e] });
        });
        return groups;
    }, [journal, typeFilter, openIncidentsOnly]);

    if (!journal) return <p>Chargement...</p>;

    const isIncident = Number(formik.values.type) === 2;
    const invalid = (n) => (formik.touched[n] && formik.errors[n] ? "is-invalid" : "");
    const alreadyLinked = new Set((photoEntry?.photos || []).map((p) => p.documentId));

    return (
        <>
            <div className="d-flex flex-wrap align-items-center mb-3">
                <div className="btn-group btn-group-sm flex-wrap mr-3 mb-2">
                    <button className={`btn ${!typeFilter ? "btn-secondary" : "btn-outline-secondary"}`} onClick={() => setTypeFilter("")}>Tout</button>
                    {TYPES_JOURNAL.map((t) => (
                        <button key={t.value} className={`btn ${Number(typeFilter) === t.value ? "btn-secondary" : "btn-outline-secondary"}`} onClick={() => setTypeFilter(String(t.value))}>
                            <i className={`fas ${t.icon} mr-1`}></i>{t.label}
                        </button>
                    ))}
                </div>
                <div className="custom-control custom-switch mr-3 mb-2">
                    <input type="checkbox" className="custom-control-input" id="openIncidents" checked={openIncidentsOnly} onChange={(e) => setOpenIncidentsOnly(e.target.checked)} />
                    <label className="custom-control-label" htmlFor="openIncidents">
                        Incidents ouverts {journal.incidentsOuverts > 0 && <span className="badge badge-danger">{journal.incidentsOuverts}</span>}
                    </label>
                </div>
                <button className="btn btn-primary ml-auto mb-2" onClick={() => openEntry(null)}><i className="fas fa-plus mr-1"></i>Nouvelle entrée</button>
            </div>

            {grouped.length === 0 && (
                <div className="text-center text-muted py-5">
                    <i className="fas fa-book-open fa-2x mb-2 d-block"></i>
                    {journal.nbEntrees === 0 ? "Le journal est vide : notez ici l'avancement, les incidents, les réunions et visites." : "Aucune entrée pour ce filtre."}
                </div>
            )}

            <div className="gp-timeline">
                {grouped.map((g) => (
                    <div key={g.key} className="gp-timeline-day">
                        <div className="gp-timeline-date">{formatDay(g.date)}</div>
                        {g.items.map((e) => {
                            const t = findTypeJournal(e.type);
                            const grav = findGravite(e.gravite);
                            const incident = t.value === 2;
                            return (
                                <div key={e.id} className={`gp-timeline-item ${incident && !e.resolu ? "gp-incident-open" : ""}`}>
                                    <div className="gp-timeline-icon" style={{ backgroundColor: t.color }}><i className={`fas ${t.icon}`}></i></div>
                                    <div className="card mb-0 flex-grow-1">
                                        <div className="card-body py-2">
                                            <div className="d-flex flex-wrap align-items-center">
                                                <span className="small text-muted mr-2">{formatTime(e.date)}</span>
                                                <strong className="mr-2">{e.titre}</strong>
                                                <span className="badge badge-light mr-1">{t.label}</span>
                                                {incident && grav && <span className={`badge ${grav.css} mr-1`}>{grav.label}</span>}
                                                {incident && (e.resolu
                                                    ? <span className="badge badge-success mr-1"><i className="fas fa-check mr-1"></i>Résolu</span>
                                                    : <span className="badge badge-outline-danger text-danger border border-danger mr-1">Ouvert</span>)}
                                                <div className="ml-auto text-nowrap">
                                                    {incident && (
                                                        <button className={`btn btn-sm ${e.resolu ? "btn-outline-secondary" : "btn-outline-success"} mr-1`} onClick={() => toggleResolu(e)}>
                                                            {e.resolu ? "Rouvrir" : "Marquer résolu"}
                                                        </button>
                                                    )}
                                                    <button className="btn btn-sm btn-outline-info mr-1" title="Photos" onClick={() => openPhotos(e)}><i className="fas fa-camera"></i></button>
                                                    <button className="btn btn-sm btn-outline-secondary mr-1" title="Modifier" onClick={() => openEntry(e)}><i className="fas fa-edit"></i></button>
                                                    <button className="btn btn-sm btn-outline-danger" title="Supprimer" onClick={() => removeEntry(e)}><i className="fas fa-trash"></i></button>
                                                </div>
                                            </div>
                                            {e.contenu && <p className="mb-1 mt-1" style={{ whiteSpace: "pre-line" }}>{e.contenu}</p>}
                                            {e.auteurNom && <div className="small text-muted"><i className="fas fa-user mr-1"></i>{e.auteurNom}</div>}
                                            {e.photos.length > 0 && (
                                                <div className="gp-journal-photos mt-2">
                                                    {e.photos.map((p) => (
                                                        <div key={p.documentId} className="gp-journal-thumb" title={p.fileName}>
                                                            {mediaKind(p) === "image" && thumbs[p.documentId]
                                                                ? <img src={thumbs[p.documentId]} alt={p.fileName} onClick={() => openPreview(p)} />
                                                                : <div className="gp-journal-file" onClick={() => openPreview(p)}>
                                                                    <i className={`fas ${mediaKind(p) === "video" ? "fa-play-circle" : "fa-file-alt"} fa-2x`}></i>
                                                                </div>}
                                                            <button className="gp-journal-thumb-remove" title="Retirer" onClick={() => unlinkPhoto(e, p)}>&times;</button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* ---- Modal entrée ---- */}
            <div className="modal fade" id={ENTRY_MODAL}>
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5><i className="fas fa-book mr-2"></i>{editing ? "Modifier l'entrée" : "Nouvelle entrée du journal"}</h5>
                            <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
                        </div>
                        <div className="modal-body">
                            <div className="btn-group btn-group-toggle d-flex flex-wrap mb-3">
                                {TYPES_JOURNAL.map((t) => (
                                    <button key={t.value} type="button"
                                        className={`btn btn-sm ${Number(formik.values.type) === t.value ? "btn-primary" : "btn-outline-primary"} flex-fill`}
                                        onClick={() => formik.setFieldValue("type", t.value)}>
                                        <i className={`fas ${t.icon} mr-1`}></i>{t.label}
                                    </button>
                                ))}
                            </div>
                            <div className="form-row">
                                <div className="form-group col-md-4">
                                    <label>Date et heure</label>
                                    <input type="datetime-local" name="date" className={`form-control ${invalid("date")}`} value={formik.values.date} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                    <div className="invalid-feedback">{formik.errors.date}</div>
                                </div>
                                <div className="form-group col-md-8">
                                    <label>Titre *</label>
                                    <input name="titre" className={`form-control ${invalid("titre")}`} value={formik.values.titre} onChange={formik.handleChange} onBlur={formik.handleBlur}
                                        placeholder={isIncident ? "ex : Fuite sur canalisation sous-sol" : "ex : Coulage dalle terminé"} />
                                    <div className="invalid-feedback">{formik.errors.titre}</div>
                                </div>
                            </div>
                            {isIncident && (
                                <div className="form-row align-items-end">
                                    <div className="form-group col-md-4">
                                        <label>Gravité</label>
                                        <select name="gravite" className="form-control" value={formik.values.gravite} onChange={formik.handleChange}>
                                            {GRAVITES.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group col-md-4">
                                        <div className="custom-control custom-switch mb-2">
                                            <input type="checkbox" className="custom-control-input" id="resolu" name="resolu" checked={!!formik.values.resolu} onChange={formik.handleChange} />
                                            <label className="custom-control-label" htmlFor="resolu">Résolu</label>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="form-group">
                                <label>Détail</label>
                                <textarea name="contenu" rows={5} className={`form-control ${invalid("contenu")}`} value={formik.values.contenu} onChange={formik.handleChange} onBlur={formik.handleBlur}
                                    placeholder="Ce qui a été fait, décidé, constaté…" />
                                <div className="invalid-feedback">{formik.errors.contenu}</div>
                            </div>
                            <div className="form-group mb-0">
                                <label>Rédigé par / présent</label>
                                <select name="auteurId" className="form-control" value={formik.values.auteurId} onChange={formik.handleChange}>
                                    <option value="">— Non précisé —</option>
                                    {employes.map((e) => <option key={e.id} value={e.id}>{e.nomComplet}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" data-dismiss="modal">Annuler</button>
                            <button type="button" className="btn btn-enregistrer" onClick={formik.handleSubmit} disabled={formik.isSubmitting}>Sauvegarder</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ---- Modal photos ---- */}
            <div className="modal fade" id={PHOTO_MODAL}>
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5>
                                <i className="fas fa-camera mr-2"></i>Photos
                                {photoEntry && <small className="text-muted d-block mt-1">{photoEntry.titre}</small>}
                            </h5>
                            <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
                        </div>
                        <div className="modal-body">
                            <h6>Envoyer de nouvelles photos / vidéos</h6>
                            <div className="form-row align-items-end">
                                <div className="form-group col-md-5">
                                    <label className="small">Dossier de la GED</label>
                                    <select className="form-control" value={folderId} onChange={(e) => setFolderId(e.target.value)}>
                                        <option value="">— Choisir le dossier —</option>
                                        {folders.slice().sort((a, b) => a.name.localeCompare(b.name)).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                                    </select>
                                </div>
                                <div className="form-group col-md-4">
                                    <label className="btn btn-outline-primary btn-block mb-0">
                                        <i className="fas fa-images mr-1"></i>{files.length ? `${files.length} fichier(s)` : "Choisir…"}
                                        <input type="file" multiple hidden accept="image/*,video/*,application/pdf"
                                            onChange={(e) => { setFiles(Array.from(e.target.files || [])); e.target.value = ""; }} />
                                    </label>
                                </div>
                                <div className="form-group col-md-3">
                                    <button className="btn btn-primary btn-block" disabled={!folderId || files.length === 0 || uploading} onClick={uploadPhotos}>
                                        {uploading ? <><i className="fas fa-spinner fa-spin mr-1"></i>Envoi…</> : <><i className="fas fa-upload mr-1"></i>Envoyer</>}
                                    </button>
                                </div>
                            </div>
                            <p className="small text-muted">Les fichiers sont classés dans la GED, rattachés au projet (galerie) et à cette entrée.</p>

                            <hr />
                            <h6>Ou choisir dans la galerie du projet</h6>
                            {projetDocs.length === 0 ? <p className="small text-muted mb-0">Aucun document dans la galerie.</p> : (
                                <div className="gp-journal-photos">
                                    {projetDocs.map((d) => {
                                        const linked = alreadyLinked.has(d.id);
                                        return (
                                            <div key={d.id} className={`gp-journal-thumb gp-pick ${linked ? "gp-picked" : ""}`} title={d.commentaire || d.fileName}
                                                onClick={() => !linked && pickExisting(d.id)}>
                                                {mediaKind(d) === "image" && thumbs[d.id]
                                                    ? <img src={thumbs[d.id]} alt={d.fileName} />
                                                    : <div className="gp-journal-file"><i className={`fas ${mediaKind(d) ? "fa-image" : "fa-file-alt"} fa-2x`}></i><small className="d-block text-truncate px-1">{d.fileName}</small></div>}
                                                {linked && <span className="gp-picked-check"><i className="fas fa-check"></i></span>}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" data-dismiss="modal">Fermer</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProjetJournal;
